import { useState } from 'react';
import { BuyBoxModals } from '@hub/common/BuyBox/modals/BuyBoxModals';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { useRouter } from 'next/router';
import { FFH_SERVICE_INT } from '@hub/free-form-hub/config';
import type {
  ProductOrigin,
  AddToBasketRequest,
  UpdateBasketRequest,
  BasketResponse,
  Procedure,
} from '@igus/iglidur-free-form-hub-interfaces';
import type { FileMetadata } from '@igus/iglidur-free-form-hub-interfaces/src/product/product';
import { ToastType, useToasts } from '@hub/hooks/useToasts';
import { useTranslation } from 'next-i18next';
import { updateSessionStore } from '@hub/libs/catalog-service';
import { getIglidurDesignerUrl, getProcedureText } from '@hub/free-form-hub/common/utils';
import { getBoreholeAndChannelCheck } from '@hub/free-form-hub/common/borehole-limitations/boreholeLimitations.utils';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { getLocale } from '@hub/libs/constants';
import { createNumberFormatter } from '@icommerce/i18n';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export type UseAddToShoppingCartDefinition = {
  session: Session;
  quantity: number;
  uploadId: string;
  origin: ProductOrigin;
  materialName: string;
  materialId?: string;
  metaData: FileMetadata | null;
  modelThumbnail?: string;
  procedure: Procedure;
  priceType?: 'binding' | 'non-binding';
};
export const useAddToShoppingCart = ({
  session,
  quantity,
  uploadId,
  origin,
  materialName,
  materialId,
  metaData,
  modelThumbnail,
  procedure,
  priceType,
}: UseAddToShoppingCartDefinition) => {
  const [activeModal, setActiveModal] = useState<BuyBoxModals | null>(null);
  const [basketData, setBasketData] = useState<BasketResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const { t, i18n } = useTranslation('free-form-hub/freeFormHubCommon');
  const { addToast } = useToasts();
  const { query } = router;
  const cartPositionId = query.edit as string;
  const boreholeAndChannelCheck = getBoreholeAndChannelCheck(metaData, procedure);
  const hasBoreholeAndChannelError = boreholeAndChannelCheck?.status === 'error';
  const [boreholeLimitationsModalVisible, setBoreholeLimitationsModalVisible] = useState(false);
  const designerUrl = metaData
    ? getIglidurDesignerUrl({
        envName: publicRuntimeConfig.envName,
        locale: getLocale(router),
        ifsId: metaData.ifsId,
        quantity,
        article: materialId ?? materialName,
      })
    : '';

  const fetchAddToBasket = async (payload: AddToBasketRequest) =>
    fetch(`${FFH_SERVICE_INT}/basket?sid=${session.sessionId}&server=${session.server}`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });

  const fetchUpdateBasket = async (payload: UpdateBasketRequest) =>
    fetch(`${FFH_SERVICE_INT}/basket?sid=${session.sessionId}&server=${session.server}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });

  const submitToShoppingCart = async () => {
    if (!session || !uploadId || !quantity || !materialName || !origin || !metaData) {
      return;
    }

    const { boundingBox } = metaData;
    const descriptionLines = [
      `${t('COMMON.CONFIGURATION', { ns: 'free-form-hub/freeFormHubCommon' })}: <configurationId>`,
      `Material: ${materialName}`,
      `Method: ${getProcedureText(procedure, t)}`,
      `x: ${boundingBox[0].toFixed(2)}mm, y: ${boundingBox[1].toFixed(2)}mm, z: ${boundingBox[2].toFixed(2)}mm`,
    ];
    if (priceType === 'non-binding') {
      descriptionLines.push(`${t('PRICE_CONFIRMATION', { ns: 'free-form-hub/freeFormHub' })}`);
    }
    if (hasBoreholeAndChannelError) {
      const formatLimit = (limit: number) => createNumberFormatter(i18n.language).format(limit);
      descriptionLines.push(
        t('BOREHOLE_LIMITATIONS.DESCRIPTION_LINE', {
          ns: 'free-form-hub/freeFormHubDetails',
          procedure,
          boreholeLimit: formatLimit(boreholeAndChannelCheck.limits.bores.warning),
          channelLimit: formatLimit(boreholeAndChannelCheck.limits.channels.warning),
        })
      );
    }
    const basketPayload: AddToBasketRequest = {
      uploadId,
      origin,
      materialName,
      quantity,
      descriptionLines,
      image: modelThumbnail,
      data: router.asPath,
    };

    setLoading(true);
    try {
      const response: Response = cartPositionId
        ? await fetchUpdateBasket({
            ...basketPayload,
            cartPositionId,
          })
        : await fetchAddToBasket(basketPayload);

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const data = (await response.json()) as BasketResponse;
      setBasketData(data);
      setActiveModal(BuyBoxModals.ADD_TO_SHOPPING_CART);
      await updateSessionStore();
    } catch (err) {
      addToast({
        content: t('COMMON.TOAST.OPERATION_FAILURE', { ns: 'free-form-hub/freeFormHubCommon' }),
        type: ToastType.ERROR,
      });
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const addToShoppingCart = async () => {
    if (hasBoreholeAndChannelError) {
      setBoreholeLimitationsModalVisible(true);
      return;
    }

    await submitToShoppingCart();
  };

  const addToShoppingCartAfterBoreholeAcknowledgement = async () => {
    setBoreholeLimitationsModalVisible(false);
    await submitToShoppingCart();
  };

  return {
    basketData,
    addToShoppingCart,
    cartPositionId,
    activeModal,
    setActiveModal,
    boreholeAndChannelCheck: hasBoreholeAndChannelError ? boreholeAndChannelCheck : null,
    boreholeLimitationsModalVisible,
    closeBoreholeLimitationsModal: () => setBoreholeLimitationsModalVisible(false),
    addToShoppingCartAfterBoreholeAcknowledgement,
    designerUrl,
    loading,
    error,
  };
};
